/**
 * creditDisplay — UI helpers สำหรับการแสดงผลเครดิต (การ์ดมุมขวา, สี, หนี้)
 * สูตรตัวเลข: utils/creditFormulas.ts (mirror API credit-formulas.ts)
 */

import {
  toDisplayCredit,
  toAmountDisplay,
  toUnitDeduct,
  getQuotaSnapshot,
  getExpiringQuota,
  getSelectionQuota,
  getCreditDebtAmountFromAvailable,
  computeAccruedDebtRaw,
} from "./creditFormulas";

export {
  toDisplayCredit,
  toAmountDisplay,
  toUnitDeduct,
  getQuotaSnapshot,
  getExpiringQuota,
  getSelectionQuota,
  getCreditDebtAmountFromAvailable,
  computeAccruedDebtRaw,
};

export const CREDIT_NEGATIVE_COLOR = "#cf1322";
export const CREDIT_NORMAL_COLOR = "#1890ff";
/** สีแถว "รับ" ในตารางประวัติเครดิต — คู่กับ CREDIT_NEGATIVE_COLOR (แถว "หัก") */
export const CREDIT_POSITIVE_COLOR = "#389e0d";

/** รวมสัญญาณหนี้จาก balance / cod-info — ใช้ค่าที่ติดลบที่สุดถ้า API ส่งไม่ตรงกัน */
export const resolveCreditDebtSnapshot = (params: {
  availableCredit?: number | null;
  availableCreditAlt?: number | null;
  hasCreditDebt?: boolean | null;
  hasCreditDebtAlt?: boolean | null;
  debtAmount?: number | null;
  debtAmountAlt?: number | null;
  debtDisplay?: number | null;
  debtDisplayAlt?: number | null;
  multiplier?: number;
}) => {
  const mult = params.multiplier ?? 2;
  const availCandidates = [params.availableCredit, params.availableCreditAlt]
    .map((v) => (v == null ? null : Number(v)))
    .filter((v): v is number => v != null && Number.isFinite(v));
  const availableRaw =
    availCandidates.length > 0 ? Math.min(...availCandidates) : 0;

  const debtAmountCandidates = [params.debtAmount, params.debtAmountAlt]
    .map((v) => (v == null ? null : Number(v)))
    .filter((v): v is number => v != null && Number.isFinite(v) && v > 0);
  const debtAmount =
    debtAmountCandidates.length > 0
      ? Math.max(...debtAmountCandidates)
      : availableRaw < 0
        ? -availableRaw
        : 0;

  const debtDisplayCandidates = [params.debtDisplay, params.debtDisplayAlt]
    .map((v) => (v == null ? null : Number(v)))
    .filter((v): v is number => v != null && Number.isFinite(v) && v > 0);
  const debtDisplay =
    debtDisplayCandidates.length > 0
      ? Math.max(...debtDisplayCandidates)
      : debtAmount > 0
        ? toDisplayCredit(debtAmount, mult)
        : 0;

  const hasCreditDebt =
    params.hasCreditDebt === true ||
    params.hasCreditDebtAlt === true ||
    availableRaw < 0 ||
    debtAmount > 0 ||
    debtDisplay > 0;

  return { availableRaw, hasCreditDebt, debtAmount, debtDisplay };
};

/** บรรทัดหลักการ์ดเครดิตมุมขวาบน — ติดลบแสดง "-{yy}" สีแดง */
export const getMainCreditLineDisplay = (params: {
  availableRaw: number;
  multiplier?: number;
  remainingQuota: number;
  debtDisplay?: number | null;
  debtAmount?: number | null;
  hasCreditDebt?: boolean;
  formatInt?: (n: number) => string;
}): { valueText: string; color: string; isNegative: boolean } => {
  const mult = params.multiplier ?? 2;
  const available = Number(params.availableRaw ?? 0);
  const fmt = params.formatInt ?? ((n: number) => Math.round(n).toLocaleString("th-TH"));

  const debtFromDisplay =
    params.debtDisplay != null && Number(params.debtDisplay) > 0
      ? Number(params.debtDisplay)
      : params.debtAmount != null && Number(params.debtAmount) > 0
        ? toDisplayCredit(Number(params.debtAmount), mult)
        : available < 0
          ? toDisplayCredit(-available, mult)
          : 0;

  const isNegative =
    params.hasCreditDebt === true || available < 0 || debtFromDisplay > 0;

  if (isNegative && debtFromDisplay > 0) {
    return {
      valueText: `-${fmt(debtFromDisplay)}`,
      color: CREDIT_NEGATIVE_COLOR,
      isNegative: true,
    };
  }

  return {
    valueText: fmt(params.remainingQuota),
    color: CREDIT_NORMAL_COLOR,
    isNegative: false,
  };
};
